import type { StoryNode } from '@/engine/types'

import { cgPath } from '../cgAssets'

// 2.1 事件一补遗（SOL-PLAN-2.1.md）。
//
// 五篇隔离补遗，统一前缀 `s1x-`。每篇只补一个来自 2.1 内容审计的最高优先级因果缺口，
// 不改变原组织路线结局或原存档结果。所有 `s1x-` 节点只在 `season1-supplement` 隔离会话中运行，
// 通过 `s1x-exit` 哨兵节点返回补遗列表；该会话不写存档、不合并收藏、不发放奖励。
//
// 事实标签（historical）：confirmed = route 文件已确认事实；adapted = 对既有时间线事件的
// 戏剧化成员视角补全；fictional = 补遗内闭合、不声称为历史记录的戏剧连接。
// 全部事件日期落在 2026-07-17 ~ 2026-07-28 事件一时间线内，未引入任何 07-28 后正式事实。

const CG_ROUTE2 = cgPath('cg34-ending-route2')
const CG_ROUTE3 = cgPath('cg23-route3-founding')
const CG_ROUTE4 = cgPath('cg24-route4-ledger')
const CG_ROUTE5 = cgPath('cg25-route5-empty-seat')
const CG_ROUTE6 = cgPath('cg26-route6-sixth-seat')

export const s1xNodes = [
  // ───────────────────────── S1X-01 二组《值得跟的人》 ─────────────────────────
  // 缺口：时 7/19 加入二组动机空白（route2 r2-02-shi-arrives）。获得方视角。
  {
    id: 's1x01-entry',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 二组',
    date: '2026-07-19',
    title: '值得跟的人',
    location: '江南 · 夜谈',
    mode: 'system',
    speaker: 'narrator',
    text: '时提着行李站在江南的门口，像是已经想了很久。你刚接任首领，组织还带着辰逸事件后的裂痕。这一夜，你决定先和他把话讲开。',
    background: 'organization',
    historical: 'adapted',
    next: 's1x01-shi-opens',
  },
  {
    id: 's1x01-shi-opens',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 二组',
    date: '2026-07-19',
    location: '江南 · 夜谈',
    mode: 'chat',
    speaker: 'shi',
    portrait: 'shi',
    text: '我转过来的事，名单上没填理由。不是因为讨厌温陷——是五组那天连“自己要成为什么”都还没定下来。而我需要一个能让我安心留下来的地方。',
    background: 'organization',
    historical: 'adapted',
    presentation: {
      cg: CG_ROUTE2,
      cgAlt: '时提着行李站在江南门口，暖光打在肩上',
      ui: 'classic',
      transition: 'ink',
      camera: 'push-in',
      atmosphere: 'none',
      focus: 'center',
    },
    next: 's1x01-player-posture',
  },
  {
    id: 's1x01-player-posture',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 二组',
    date: '2026-07-19',
    location: '江南 · 夜谈',
    mode: 'chat',
    speaker: 'player',
    text: '你打算怎么接住这个人？',
    background: 'organization',
    historical: 'adapted',
    choices: [
      {
        id: 'give-seat',
        label: '“二组现在缺能扛事的人，你来得正好。”',
        tone: 'warm',
        effects: [
          { type: 'variable', key: 's1x01Trust', value: 'mid' },
          { type: 'relationship', character: 'shi', key: 'trust', value: 1 },
        ],
        next: 's1x01-react-seat',
      },
      {
        id: 'give-truth',
        label: '“我知道你从五组来，那边发生了什么？”',
        tone: 'calm',
        effects: [
          { type: 'variable', key: 's1x01Trust', value: 'high' },
          { type: 'relationship', character: 'shi', key: 'trust', value: 2 },
        ],
        next: 's1x01-react-truth',
      },
      {
        id: 'give-boundary',
        label: '“先观察一阵，证明你能留再说。”',
        tone: 'secret',
        effects: [{ type: 'variable', key: 's1x01Trust', value: 'low' }],
        next: 's1x01-react-boundary',
      },
    ],
  },
  {
    id: 's1x01-react-seat',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 二组',
    date: '2026-07-19',
    location: '江南 · 夜谈',
    mode: 'chat',
    speaker: 'shi',
    portrait: 'shi',
    text: '“来得正好”这话别人也说过。但你是第一个把它当真的人。那我先干着，看这地方值不值得我留下。',
    background: 'organization',
    historical: 'adapted',
    next: 's1x01-close',
  },
  {
    id: 's1x01-react-truth',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 二组',
    date: '2026-07-19',
    location: '江南 · 夜谈',
    mode: 'chat',
    speaker: 'shi',
    portrait: 'shi',
    text: '你肯问，我就肯说。五组不是不好，是还在找自己。二组这边，你刚接手就肯把规矩写清楚——这种“确定性”，是我最想跟的东西。',
    background: 'organization',
    historical: 'adapted',
    next: 's1x01-close',
  },
  {
    id: 's1x01-react-boundary',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 二组',
    date: '2026-07-19',
    location: '江南 · 夜谈',
    mode: 'chat',
    speaker: 'shi',
    portrait: 'shi',
    text: '观察我？也行。我不急着要谁信任，先看看你这个首领是不是真把人当人。',
    background: 'organization',
    historical: 'adapted',
    next: 's1x01-close',
  },
  {
    id: 's1x01-close',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 二组',
    date: '2026-07-19',
    location: '江南 · 夜谈',
    mode: 'system',
    speaker: 'narrator',
    text: '时留了下来。二组“凭什么值得跟”的命题，在这一夜有了第一个答案：不是因为大，而是因为肯把人接住。',
    background: 'organization',
    historical: 'adapted',
    onEnter: [{ type: 'flag', key: 's1x01_done' }],
    next: 's1x-exit',
  },

  // ───────────────────────── S1X-02 三组《两次转组的人》 ─────────────────────────
  // 缺口：告白 7/28 转一组动机空白（route3 r3-19-gaobai-leaves）。confirmed 事件 + adapted 动因。
  {
    id: 's1x02-entry',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 三组',
    date: '2026-07-28',
    title: '两次转组的人',
    location: '抚梅观清雪 · 门口',
    mode: 'system',
    speaker: 'narrator',
    text: '告白来辞行。他两天前才进三组，现在又要走。心跳成瘾不在，你作为三组首领，决定最后问一句。',
    background: 'organization',
    historical: 'confirmed',
    next: 's1x02-player-choice',
  },
  {
    id: 's1x02-player-choice',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 三组',
    date: '2026-07-28',
    location: '抚梅观清雪 · 门口',
    mode: 'chat',
    speaker: 'player',
    text: '他站在门口，手里攥着一张手写告别。',
    background: 'organization',
    historical: 'confirmed',
    presentation: {
      cg: CG_ROUTE3,
      cgAlt: '告白在三组门口攥着一张手写告别',
      ui: 'classic',
      transition: 'crossfade',
      camera: 'pull-back',
      atmosphere: 'petals',
      focus: 'center',
    },
    choices: [
      {
        id: 'let-go',
        label: '“你来了又走，三组都接得住。”',
        tone: 'warm',
        effects: [{ type: 'flag', key: 's1x02_gaobaiReasonKnown', value: false }],
        next: 's1x02-react-letgo',
      },
      {
        id: 'ask-why',
        label: '“至少说清楚，为什么再走一次？”',
        tone: 'calm',
        effects: [{ type: 'flag', key: 's1x02_gaobaiReasonKnown', value: true }],
        next: 's1x02-react-why',
      },
    ],
  },
  {
    id: 's1x02-react-letgo',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 三组',
    date: '2026-07-28',
    location: '抚梅观清雪 · 门口',
    mode: 'chat',
    speaker: 'gaobai',
    portrait: 'gaobai',
    text: '你说得对，吸收不是拥有。那我走了——但这次，我没瞒你。',
    background: 'organization',
    historical: 'confirmed',
    next: 's1x02-close',
  },
  {
    id: 's1x02-react-why',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 三组',
    date: '2026-07-28',
    location: '抚梅观清雪 · 门口',
    mode: 'chat',
    speaker: 'gaobai',
    portrait: 'gaobai',
    text: '一组给了我一个“只属于写作者、不被任何组织裹挟”的承诺。我两次转组都不是背叛，只是每次都在找能让我安心写东西的地方。这次，我找到了。',
    background: 'organization',
    historical: 'adapted',
    next: 's1x02-close',
  },
  {
    id: 's1x02-close',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 三组',
    date: '2026-07-28',
    location: '抚梅观清雪 · 门口',
    mode: 'system',
    speaker: 'narrator',
    text: '告白转去了一组。三组“人来人走不等于组织死亡”的哲学，在这最后一次告别里被他自己印证了。',
    background: 'organization',
    historical: 'confirmed',
    onEnter: [{ type: 'flag', key: 's1x02_done' }],
    next: 's1x-exit',
  },

  // ───────────────────────── S1X-03 四组《空白账本之前》 ─────────────────────────
  // 缺口：砚秋水为何创立四组、为何把首领给玩家（route4 r4-00-founded）。confirmed + adapted。
  {
    id: 's1x03-entry',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 四组',
    date: '2026-07-19',
    title: '空白账本之前',
    location: '天上白玉京 · 创立前夜',
    mode: 'system',
    speaker: 'narrator',
    text: '砚秋水找你，说要开第四个组织。账本还没写第一笔，她先问了你两个问题。',
    background: 'organization',
    historical: 'confirmed',
    next: 's1x03-player-ask',
  },
  {
    id: 's1x03-player-ask',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 四组',
    date: '2026-07-19',
    location: '天上白玉京 · 创立前夜',
    mode: 'chat',
    speaker: 'player',
    text: '你想先问哪一个？',
    background: 'organization',
    historical: 'confirmed',
    presentation: {
      cg: CG_ROUTE4,
      cgAlt: '空白账本摊开在木桌上，灯火将落未落',
      ui: 'classic',
      transition: 'crossfade',
      camera: 'push-in',
      atmosphere: 'none',
      focus: 'center',
    },
    choices: [
      {
        id: 'ask-why-founded',
        label: '“为什么要再开一个组织？”',
        tone: 'calm',
        effects: [{ type: 'variable', key: 's1x03Tone', value: 'principle' }],
        next: 's1x03-react-founded',
      },
      {
        id: 'ask-why-you',
        label: '“为什么是你把首领给我？”',
        tone: 'warm',
        effects: [{ type: 'variable', key: 's1x03Tone', value: 'trust' }],
        next: 's1x03-react-you',
      },
    ],
  },
  {
    id: 's1x03-react-founded',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 四组',
    date: '2026-07-19',
    location: '天上白玉京 · 创立前夜',
    mode: 'chat',
    speaker: 'yanqiu',
    portrait: 'yanqiu',
    text: '五大组织成型后，我看见的是“每个组都在用故事绑架人”。我要开一个反例——首领也得被账本管着，谁都不能靠威信压人。',
    background: 'organization',
    historical: 'adapted',
    next: 's1x03-close',
  },
  {
    id: 's1x03-react-you',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 四组',
    date: '2026-07-19',
    location: '天上白玉京 · 创立前夜',
    mode: 'chat',
    speaker: 'yanqiu',
    portrait: 'yanqiu',
    text: '辰逸事件后，只有你愿意把规则写下来，而不是靠威信压人。一个肯被账本约束的人，才配得上“首领”这两个字。',
    background: 'organization',
    historical: 'adapted',
    next: 's1x03-close',
  },
  {
    id: 's1x03-close',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 四组',
    date: '2026-07-19',
    location: '天上白玉京 · 创立前夜',
    mode: 'system',
    speaker: 'narrator',
    text: '账本第一笔落下。天上白玉京存在的理由，被写成了“规则约束首领”——这比任何宣言都更难被推翻。',
    background: 'organization',
    historical: 'confirmed',
    onEnter: [{ type: 'flag', key: 's1x03_done' }],
    next: 's1x-exit',
  },

  // ───────────────────────── S1X-04 五组《第一天就走了的人》 ─────────────────────────
  // 缺口：时 7/19 离开五组动机空白（route5 r5-01-shi-leaves）+ 温陷余波。adapted，与 S1X-01 同源。
  {
    id: 's1x04-entry',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 五组',
    date: '2026-07-19',
    title: '第一天就走了的人',
    location: '未闻花名 · 空位',
    mode: 'system',
    speaker: 'narrator',
    text: '时转组走的那天，五组才刚成立。温陷看着那个空着的座位，把话咽了回去。你站在他旁边，决定怎么面对这件事。',
    background: 'organization',
    historical: 'adapted',
    next: 's1x04-player-choice',
  },
  {
    id: 's1x04-player-choice',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 五组',
    date: '2026-07-19',
    location: '未闻花名 · 空位',
    mode: 'chat',
    speaker: 'player',
    text: '那个座位还留着时搭过的外套。',
    background: 'organization',
    historical: 'adapted',
    presentation: {
      cg: CG_ROUTE5,
      cgAlt: '五组里一个空着的座位，椅背上搭着一件没带走的围巾',
      ui: 'classic',
      transition: 'crossfade',
      camera: 'pull-back',
      atmosphere: 'none',
      focus: 'center',
    },
    choices: [
      {
        id: 'let-him-go',
        label: '“去他能扎根的地方吧。”',
        tone: 'warm',
        effects: [{ type: 'flag', key: 's1x04_shiReasonShared', value: false }],
        next: 's1x04-react-go',
      },
      {
        id: 'ask-why',
        label: '“至少告诉我，他为什么走。”',
        tone: 'calm',
        effects: [{ type: 'flag', key: 's1x04_shiReasonShared', value: true }],
        next: 's1x04-react-why',
      },
    ],
  },
  {
    id: 's1x04-react-go',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 五组',
    date: '2026-07-19',
    location: '未闻花名 · 空位',
    mode: 'chat',
    speaker: 'wenxian',
    portrait: 'wenxian',
    text: '你说得轻巧。可我是首领，连“为什么”都没问出口。这座位，我先替他留着。',
    background: 'organization',
    historical: 'adapted',
    next: 's1x04-close',
  },
  {
    id: 's1x04-react-why',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 五组',
    date: '2026-07-19',
    location: '未闻花名 · 空位',
    mode: 'chat',
    speaker: 'wenxian',
    portrait: 'wenxian',
    text: '他没讨厌五组。是五组那天连“要成为什么”都没定，他需要一个确定的地方。二组那边，给了他这个确定性。怪不得他，也怪不得我——只能怪我们慢了一步。',
    background: 'organization',
    historical: 'adapted',
    next: 's1x04-close',
  },
  {
    id: 's1x04-close',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 五组',
    date: '2026-07-19',
    location: '未闻花名 · 空位',
    mode: 'system',
    speaker: 'narrator',
    text: '温陷没把座位收走。“未闻花名”后来学会了：战败后仍算存在，慢一步也不是结束。',
    background: 'organization',
    historical: 'adapted',
    onEnter: [{ type: 'flag', key: 's1x04_done' }],
    next: 's1x-exit',
  },

  // ───────────────────────── S1X-05 六组《第六个为什么》 ─────────────────────────
  // 缺口：玩家（首领）为何建立第六个组织（route6 r6-00-founded）。adapted，唯一玩家自身即动机主体。
  {
    id: 's1x05-entry',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 六组',
    date: '2026-07-19',
    title: '第六个为什么',
    location: '第六席 · 创立那一刻',
    mode: 'system',
    speaker: 'narrator',
    text: 'yyT 把笔递给你：“名字是你建的，首领也由你当。但你得先说清楚——第六个组织，到底反对什么？”',
    background: 'sixthSeat',
    historical: 'adapted',
    next: 's1x05-player-charter',
  },
  {
    id: 's1x05-player-charter',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 六组',
    date: '2026-07-19',
    location: '第六席 · 创立那一刻',
    mode: 'chat',
    speaker: 'player',
    text: '章程第一页，你写下反对的那一条。',
    background: 'sixthSeat',
    historical: 'adapted',
    presentation: {
      cg: CG_ROUTE6,
      cgAlt: '玩家在“第六席”的章程首页签下名字',
      ui: 'classic',
      transition: 'ink',
      camera: 'push-in',
      atmosphere: 'none',
      focus: 'center',
    },
    choices: [
      {
        id: 'anti-story',
        label: '“反对把人写成剧情工具。”',
        tone: 'bold',
        effects: [{ type: 'variable', key: 's1x05Tone', value: 'antiStory' }],
        next: 's1x05-react-story',
      },
      {
        id: 'anti-authority',
        label: '“反对首领即权威。”',
        tone: 'bold',
        effects: [{ type: 'variable', key: 's1x05Tone', value: 'antiAuthority' }],
        next: 's1x05-react-authority',
      },
      {
        id: 'anti-exclusive',
        label: '“反对成员只能属于一个组织。”',
        tone: 'bold',
        effects: [{ type: 'variable', key: 's1x05Tone', value: 'antiExclusive' }],
        next: 's1x05-react-exclusive',
      },
    ],
  },
  {
    id: 's1x05-react-story',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 六组',
    date: '2026-07-19',
    location: '第六席 · 创立那一刻',
    mode: 'chat',
    speaker: 'yyt',
    portrait: 'yyt',
    text: '把人当人，不把成员写成剧情工具——这条我替你记下了。第六席，从这一笔开始。',
    background: 'sixthSeat',
    historical: 'adapted',
    next: 's1x05-close',
  },
  {
    id: 's1x05-react-authority',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 六组',
    date: '2026-07-19',
    location: '第六席 · 创立那一刻',
    mode: 'chat',
    speaker: 'yyt',
    portrait: 'yyt',
    text: '首领也要被规则管——这话从你嘴里说出来，第六席就站得住。我替你记下了。',
    background: 'sixthSeat',
    historical: 'adapted',
    next: 's1x05-close',
  },
  {
    id: 's1x05-react-exclusive',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 六组',
    date: '2026-07-19',
    location: '第六席 · 创立那一刻',
    mode: 'chat',
    speaker: 'yyt',
    portrait: 'yyt',
    text: '不要求谁只属于一个组织——这条很新，也很对。第六席，从这一笔开始。',
    background: 'sixthSeat',
    historical: 'adapted',
    next: 's1x05-close',
  },
  {
    id: 's1x05-close',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 六组',
    date: '2026-07-19',
    location: '第六席 · 创立那一刻',
    mode: 'system',
    speaker: 'narrator',
    text: '第六个组织亮了起来。它存在的理由，由你亲手写在第一页——反对什么，比宣布什么更重要。',
    background: 'sixthSeat',
    historical: 'adapted',
    onEnter: [{ type: 'flag', key: 's1x05_done' }],
    next: 's1x-exit',
  },

  // ───────────────────────── 补遗六：一组 · 第一席为什么是第一席 ─────────────────────────
  {
    id: 's1x06-entry',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 一组',
    date: '2026-07-17',
    title: '第一席为什么是第一席',
    location: '群雄逐鹿 · 创立日',
    mode: 'novel',
    speaker: 'narrator',
    text: '群雄逐鹿成立的第一天，祈福只在群里发了一条消息：“谁先到20级，谁是高层。”那一天还没结束，顶级奶瓶的名字已经从学员跳到了高层栏。',
    background: 'organization',
    historical: 'adapted',
    next: 's1x06-player-ask',
  },
  {
    id: 's1x06-player-ask',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 一组',
    date: '2026-07-17',
    location: '群雄逐鹿 · 创立日',
    mode: 'chat',
    speaker: 'bottle',
    portrait: 'bottle',
    text: '有人问顶级奶瓶为什么这么拼。奶瓶说：“第一席不是抢来的位置——是第一个证明自己不怕输的组织。”\n祈福没有回应这句话，但第二天把“秩序与代表权”写进了组织公告。',
    background: 'organization',
    historical: 'adapted',
    choices: [
      {
        id: 's1x06-ask-order',
        tone: 'calm',
        label: '“你们从一开始就知道要当第一席？”',
        effects: [
          { type: 'variable', key: 's1x06Tone', value: 'order' },
          { type: 'relationship', character: 'qifu', key: 'trust', value: 1 },
        ],
        next: 's1x06-react-order',
      },
      {
        id: 's1x06-ask-speak',
        tone: 'bold',
        label: '“如果有一天你们不是第一了，秩序还在不在？”',
        effects: [
          { type: 'variable', key: 's1x06Tone', value: 'speak' },
          { type: 'relationship', character: 'bottle', key: 'trust', value: 1 },
        ],
        next: 's1x06-react-speak',
      },
    ],
  },
  {
    id: 's1x06-react-order',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 一组',
    date: '2026-07-17',
    location: '群雄逐鹿 · 创立日',
    mode: 'chat',
    speaker: 'qifu',
    portrait: 'qifu',
    text: '祈福关掉了所有群聊：“不是要当第一。是如果没有一个能说话的组织，这个区第一天就会乱。”他的声音很平，但消息末尾多加了一个句号——那是祈福唯一一次多打标点。',
    background: 'organization',
    historical: 'adapted',
    next: 's1x06-close',
  },
  {
    id: 's1x06-react-speak',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 一组',
    date: '2026-07-17',
    location: '群雄逐鹿 · 创立日',
    mode: 'chat',
    speaker: 'bottle',
    portrait: 'bottle',
    text: '顶级奶瓶在频道里打了三行字又删了两行。最后留下的一句是：“秩序不是第一名给的——是第一个站出来的人建的。”\n祈福在下面回了个“1”。',
    background: 'organization',
    historical: 'adapted',
    next: 's1x06-close',
  },
  {
    id: 's1x06-close',
    chapter: 'epilogue',
    actLabel: '事件一补遗 · 一组',
    date: '2026-07-17',
    location: '群雄逐鹿 · 创立日',
    mode: 'system',
    speaker: 'narrator',
    text: '群雄逐鹿的组织公告栏上，“秩序与代表权”五个字挂在那里。后来所有组织都在效仿，但没有一个字比祈福打的第一个句号更重。',
    background: 'organization',
    historical: 'adapted',
    onEnter: [{ type: 'flag', key: 's1x06_done' }],
    next: 's1x-exit',
  },

  // ───────────────────────── 补遗返回哨兵 ─────────────────────────
  // 所有补遗收束节点 next 指向此处；season1-supplement 会话在 transition 中拦截并返回补遗列表。
  // 该节点本身仅供内容图校验存在性，永不真正进入（store 在抵达前已退出回放）。
  {
    id: 's1x-exit',
    chapter: 'epilogue',
    actLabel: '事件一补遗',
    date: '2026-07-28',
    title: '返回补遗列表',
    location: '4945区',
    mode: 'system',
    speaker: 'narrator',
    text: '（返回补遗列表）',
    background: 'login',
    historical: 'fictional',
  },
] satisfies StoryNode[]
